import * as EncampCommunication from "encamp-communication/client";
import { useMemo } from "react";

import { useCommunicationFormContext } from "./useCommunicationForm";

// Extracts all unique variable names from a template string (e.g., {{firstName}})
export function extractTemplateVariables(template: string): string[] {
  const regex = /\{\{(\w+)\}\}/g;
  const found = new Set<string>();
  let match;
  while ((match = regex.exec(template)) !== null) {
    found.add(match[1]);
  }
  return Array.from(found);
}

export type TemplateVariable = {
  name: string;
  value: string;
};

export type UseTemplateHydrationReturn = {
  templateVariables: TemplateVariable[];
};

/**
 * Simple React hook for template editing and hydration
 */
export function useTemplateHydration() {
  const { watch } = useCommunicationFormContext();

  const tenant = watch("tenant");
  const facilities = watch("facilities");
  const tierIIReports = watch("tierIIReports");
  const submissions = watch("submissions");

  const template = useMemo(
    () => EncampCommunication.getTemplate(EncampCommunication.TemplateName.MailRoomFlexible),
    []
  );

  const templateVariables = useMemo(() => {
    if (!tenant) {
      return [];
    }

    const variables = template.extractVariables({
      communication: {
        tenant,
        facilities:
          facilities?.map((f) => ({
            ...f,
            streetAddress1: f.streetAddress1 ?? null,
            city: f.city ?? null,
            state: f.state ?? null,
            zip: f.zip ?? null,
          })) ?? [],
        tierIIReports,
        submissions: submissions ?? [],
      },
    });
    return Object.entries(variables).map(([name, value]) => ({
      name,
      value,
    }));
  }, [template, tenant, facilities, tierIIReports, submissions]);

  return { templateVariables };
}
